/**
 * @file Полноценный игровой стол для онлайн-белота (армянский / молдавский).
 *
 * Делает то, чего не было в `BaseGameRoomScreen` ранее: эллиптическая
 * рассадка `IndustrialSeat` × 4, центр взятки с картами через
 * `PokerTableSurface`, фан-рука героя с hover-lift, бейджи дилера/контракта,
 * счётчики команд. Поведенческие детали (drag&drop карт, авто-PASS,
 * declarations vote, hardcore declarations) сюда не тащим — всё, что
 * требует серверной поддержки, остаётся за `BaseGameRoomScreen`/сервером.
 *
 * Дизайн-язык — 1:1 с локальным
 * {@link client/src/components/belot/ArmenianBelotTablePresentation.jsx}
 * (тот же `IndustrialSeat`, `PokerTableSurface`, `TRICK_SLOT_TRANSFORM`,
 * tone-функция мастности), чтобы у пользователей не было визуального
 * перепада между local и online белотом.
 *
 * @module components/online/OnlineBelotTable
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import gsap from 'gsap';
import { Eye, Timer, Braces } from 'lucide-react';
import { cn } from '../../poker-ui/utils/cn';
import { PokerTableSurface } from '../../poker-ui/PokerTableSurface';
import { PlayingCard } from '../../poker-ui/PlayingCard';
import { IndustrialSeat } from '../../poker-ui/IndustrialSeat';
import {
  auctionTrumpBubbleGlyph,
  belotCardColorClass,
  belotCardIdToPlaying,
  belotCardIsTrump,
  belotContractCpLabelFromEngine,
  belotMatchCpFromEnginePoints,
  belotMatchTargetCpFromEngine,
  bidCpTensForDisplay,
  formatBelotTrumpBid,
  sortBelotHandForDisplay,
} from '../../game/belotArmenianCards';
import {
  TRICK_SLOT_TRANSFORM,
  getRelativeSeatPosition,
  largeTrumpDisplay,
  belotTrumpGlyphFaceClass,
  belotSpeechTonePairFromGlyphLine,
  BelotVividSuitInlineText,
  BELOT_FELT_LABEL_NEUTRAL,
  trumpBadgeToneByGlyph,
  BELOT_ONLINE_SEAT_ELLIPSE_PRESETS,
  BELOT_ONLINE_SEAT_MOBILE_STACK_PRESETS,
} from '../belot/BelotTableSharedUi';
import { BelotDeckTrumpIndicator } from '../belot/BelotDeckTrumpIndicator';
import { BelotAuctionSeatActionBubble } from '../belot/BelotAuctionSeatActionBubble';
import { BelotAuctionMovesRiver } from '../belot/BelotAuctionMovesRiver';
import { BelotSeatSignalStrip } from '../belot/BelotSeatSignalStrip';
import { collectDeclarePillLabels, SeatBelotMatchDropBadge } from '../belot/belotMatchSeatDropBadge';
import {
  hasRevealableDeclaredComboCardIds,
  parseDeclaredComboKey,
  sortDeclaredComboKeysForRevealDescending,
} from '../belot/belotComboLabelUi';
import { useBelotOnlineDealAnimation } from './hooks/useBelotOnlineDealAnimation';
import { useBelotOnlinePlayCardFlight } from './hooks/useBelotOnlinePlayCardFlight';
import { useBelotOnlineHeroDragPlay } from './hooks/useBelotOnlineHeroDragPlay';
import { useBelotOnlineTrickEndAnimation } from './hooks/useBelotOnlineTrickEndAnimation';
import { useBelotShulerCaughtFx } from './hooks/useBelotShulerCaughtFx';
import { useBelotVisualFxQueue, BELOT_FX_PRIORITY } from './hooks/useBelotVisualFxQueue';
import {
  BELOT_FX_Z_COMBO_FAN,
  BELOT_FX_Z_CRITICAL_OVERLAY,
  BELOT_FX_Z_SPEECH_BUBBLE,
  BELOT_FX_Z_TABLE,
  BELOT_FX_FUN_EMOJI,
} from './belotFxLayers';
import { BelotFxBubble } from '../belot/BelotFxBubble';
import { BelotOnlineRevealOverlay } from './BelotOnlineRevealOverlay';
import {
  belotAdvanceBelaSpeechState,
  belotFreshBelaRoyalsByTeam,
  belotSeatBubblePlacement,
} from '../../game/belotBelaFx';

const {
  resolveDeclarationBattleOutcome,
  hasFullHandsSnapshot,
} = require('../../game/belotArmenian/resolveDeclarationBattleOutcome');

const HUMAN_SEAT_FALLBACK = 0;

const SEAT_ELLIPSE_PRESETS = BELOT_ONLINE_SEAT_ELLIPSE_PRESETS;
const SEAT_MOBILE_STACK_PRESETS = BELOT_ONLINE_SEAT_MOBILE_STACK_PRESETS;

const SEAT_ACTION_TTL_MS = 3600;

function teamOfSeat(seat) {
  return seat === 0 || seat === 2 ? 0 : 1;
}

function seatDisplayNameForOnline(seat, heroSeat, heroName, belotSeatRows) {
  const row = belotSeatRows?.find?.((r) => r.seat === seat);
  if (seat === heroSeat) return heroName;
  return row?.name || `м${seat}`;
}

/** @param {{ seat: number, card: number, dirty?: boolean, caughtResolved?: boolean }[]} plays */
function playsToOnlineTrickUiRows(plays, heroSeat, heroName, belotSeatRows) {
  if (!Array.isArray(plays)) return [];
  return plays.map((p) => ({
    seat: p.seat,
    key: `${p.seat}-${p.card}`,
    label: seatDisplayNameForOnline(p.seat, heroSeat, heroName, belotSeatRows),
    dirty: Boolean(p.dirty) && !p.caughtResolved,
    ...belotCardIdToPlaying(p.card),
  }));
}

function SeatLastMoveBadge({ action, compact = false }) {
  if (!action?.kind) return null;
  let short = action.label || '';
  let tone =
    'border-border/80 bg-muted/25 text-foreground/90 hover:border-neon-cyan/40 hover:bg-muted/45 hover:shadow-[0_0_12px_rgba(0,255,255,0.12)]';
  switch (action.kind) {
    case 'pass':
      short = action.label || 'Пас';
      tone = 'border-red-500/35 bg-red-950/25 text-red-100/95';
      break;
    case 'bid':
      short = action.label || 'Бид';
      tone = 'border-violet-400/35 bg-violet-950/25 text-violet-100/95';
      break;
    case 'contra':
      short = action.label || 'Контра';
      tone = 'border-amber-400/40 bg-amber-950/25 text-amber-100/95';
      break;
    case 'recontra':
      short = action.label || 'Реконтра';
      tone = 'border-amber-300/60 bg-amber-600/25 text-amber-50';
      break;
    case 'play':
      short = action.label || 'Ход';
      tone = 'border-cyan-500/35 bg-cyan-950/20 text-cyan-50/95';
      break;
    case 'trick':
      short = action.label || 'Взял';
      tone = 'border-emerald-500/40 bg-emerald-950/20 text-emerald-50/95';
      break;
    default:
      break;
  }
  return (
    <div
      className={cn(
        'flex max-w-[92px] cursor-default select-none items-center justify-center rounded-md border',
        compact ? 'px-1.5 py-[1px] text-[9px]' : 'px-2 py-0.5 text-[10px]',
        'font-semibold leading-tight shadow-sm outline-none transition',
        tone,
      )}
      title={short}
    >
      <span className="truncate">{short}</span>
    </div>
  );
}

/**
 * Вычисляет геометрию веера руки для эритроцитов 1-12 карт.
 * Скопирована из `useBelotHandFanMetrics` локального лобби, чтобы избежать
 * перекрёстных импортов внутри `client/src/components/belot/hooks/`.
 */
function computeHandFanMetrics(handSize, compact = false) {
  const handCount = Math.max(1, handSize);
  const fanStep = compact
    ? Math.max(20, Math.min(36, 232 / Math.max(1, handCount - 1)))
    : Math.max(24, Math.min(44, 280 / Math.max(1, handCount - 1)));
  const fanWidth = (handCount - 1) * fanStep + 80;
  const fanBaseYOffset = compact ? 12 : 16;
  return { handCount, fanStep, fanWidth, fanBaseYOffset };
}

/**
 * @param {object} props
 * @param {object|null} props.belotArmenian
 * @param {Array<{ seat: number, name: string, uid?: string, avatarUrl?: string }>} props.belotSeatRows
 * @param {number|null} props.mySeat
 * @param {number[]} props.myHand
 * @param {(card: number) => void} props.onPlayCard
 * @param {boolean} props.canPlay
 * @param {React.MutableRefObject<HTMLElement|null>} [props.contractBadgeAnchorRef]
 * @param {React.MutableRefObject<HTMLElement|null>} [props.deckRef]
 * @param {React.MutableRefObject<HTMLElement|null>} [props.trickCenterRef]
 * @param {React.Ref<HTMLElement|null>} [props.heroSeatAnchorRef] — DOM места героя (FX объявления комбо).
 * @param {number|null} [props.heroSecLeft]
 * @param {number} [props.heroTurnSeconds]
 * @param {() => void} [props.onPeekLastTrick]
 * @param {() => void} [props.onOpenScoreSheet]
 * @param {string} [props.heroName]
 * @param {number} [props.targetScore]
 * @param {boolean} [props.compact]
 * @param {React.ReactNode} [props.statusBanner]
 * @param {React.ReactNode} [props.bottomActions]
 * @param {React.ReactNode} [props.heroAuctionPanel] — панель торгов над веером (`ArmenianAuctionPanel` + `presentation="inline"`).
 * @param {boolean} [props.suppressed] — legacy: то же что `audioSuppressed`, если он не передан.
 * @param {boolean} [props.audioSuppressed]
 * @param {boolean} [props.suppressDealAnimation]
 * @param {(active: boolean) => void} [props.onRoundDealAnimActiveChange]
 * @param {(active: boolean) => void} [props.onPlayFlightActiveChange]
 * @param {number} [props.belotPlayResyncKey]
 * @param {number} [props.belotSyncSeq] — монотонный seq из `belotSync` (FX шулера без повтора при реконнекте).
 * @param {(opts: { cardIds: number[], label?: string, launchAnchorRef?: { current: HTMLElement|null } }) => Promise<void>} [props.runQueuedComboCardReveal] — публичный ревил хардкора через очередь (портал в родителе).
 * @param {(enqueue: null | ((item: { id: string, priority: number, run: () => void | Promise<void>, minGapMs?: number }) => void)) => void} [props.onRegisterHardcoreSelfPreviewEnqueue] — регистрация `visualFxQueue.enqueue` для пре-коммит превью в родителе.
 */
export function OnlineBelotTable({
  belotArmenian,
  belotSeatRows,
  mySeat,
  myHand,
  onPlayCard,
  canPlay,
  contractBadgeAnchorRef,
  deckRef,
  trickCenterRef,
  heroSeatAnchorRef = null,
  heroSecLeft = null,
  heroTurnSeconds = 30,
  onPeekLastTrick,
  onOpenScoreSheet,
  heroName = 'Вы',
  targetScore,
  compact = null,
  statusBanner = null,
  bottomActions = null,
  heroAuctionPanel = null,
  play = () => {},
  suppressed = false,
  audioSuppressed: audioSuppressedProp,
  suppressDealAnimation = false,
  onRoundDealAnimActiveChange,
  onPlayFlightActiveChange,
  belotPlayResyncKey = 0,
  belotSyncSeq = 0,
  shulerEnabled = false,
  shulerLegalCardIds = null,
  onAccuseDirty,
  runQueuedComboCardReveal = null,
  onRegisterHardcoreSelfPreviewEnqueue = null,
}) {
  const audioSuppressed = audioSuppressedProp !== undefined ? audioSuppressedProp : suppressed;
  const [autoCompact, setAutoCompact] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const update = () => setAutoCompact(window.innerWidth <= 820);
    update();
    window.addEventListener('resize', update);
    window.addEventListener('orientationchange', update);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('orientationchange', update);
    };
  }, []);
  const isCompact = typeof compact === 'boolean' ? compact : autoCompact;
  const heroIndex = mySeat != null ? mySeat : HUMAN_SEAT_FALLBACK;
  const phase = belotArmenian?.phase || '';
  const inAuction = phase === 'auction';
  const inDeclare = phase === 'declare';
  const inPlay = phase === 'play';
  const turnSeat =
    typeof belotArmenian?.currentTurnSeat === 'number' ? belotArmenian.currentTurnSeat : null;
  const dealerSeat =
    typeof belotArmenian?.dealerSeat === 'number' ? belotArmenian.dealerSeat : null;
  const contract = belotArmenian?.contract || null;
  const auction = belotArmenian?.auction || null;
  const tricksTakenByTeam = useMemo(() => {
    const t = belotArmenian?.tricksTakenByTeam;
    if (!Array.isArray(t)) return [0, 0];
    return [Number(t[0]) || 0, Number(t[1]) || 0];
  }, [belotArmenian?.tricksTakenByTeam]);
  const tricksTakenTotal = useMemo(
    () => (Number(tricksTakenByTeam[0]) || 0) + (Number(tricksTakenByTeam[1]) || 0),
    [tricksTakenByTeam],
  );
  const teamScores = useMemo(() => {
    const t = belotArmenian?.teamScores;
    if (!Array.isArray(t)) return [0, 0];
    return [Number(t[0]) || 0, Number(t[1]) || 0];
  }, [belotArmenian?.teamScores]);
  const currentTrick = useMemo(() => {
    const c = belotArmenian?.currentTrick;
    return Array.isArray(c) ? c : [];
  }, [belotArmenian?.currentTrick]);
  const currentTrickLen = currentTrick.length;
  const lastCompleted = belotArmenian?.lastCompletedTrick || null;
  /** Сервер обнуляет `lastCompletedTrick` на новой раздаче (bazar); держим копию как в локальном `lastTrickSnapshot`. */
  const [persistedLastTrickStrip, setPersistedLastTrickStrip] = useState(
    /** @type {null | { plays: { seat: number, card: number }[], winnerSeat: number, completedIdx: number }} */ (null),
  );
  useEffect(() => {
    const lc = belotArmenian?.lastCompletedTrick;
    if (lc && Array.isArray(lc.plays) && lc.plays.length === 4) {
      const ws = Number(lc.winnerSeat);
      setPersistedLastTrickStrip({
        plays: lc.plays.map((p) => ({ seat: Number(p.seat), card: Number(p.card) })),
        winnerSeat: Number.isInteger(ws) && ws >= 0 && ws <= 3 ? ws : 0,
        completedIdx: Number(lc.trickIndex) || 0,
      });
    }
  }, [belotArmenian?.lastCompletedTrick]);
  useEffect(() => {
    if (inPlay && currentTrick.length > 0) {
      setPersistedLastTrickStrip(null);
    }
  }, [inPlay, currentTrick.length]);
  useEffect(() => {
    if (!belotArmenian) setPersistedLastTrickStrip(null);
  }, [belotArmenian]);
  const lastTrickStripDisplay = useMemo(() => {
    if (inPlay && currentTrick.length > 0) return null;
    const lct =
      lastCompleted && Array.isArray(lastCompleted.plays) && lastCompleted.plays.length === 4
        ? lastCompleted
        : persistedLastTrickStrip;
    if (!lct || !Array.isArray(lct.plays) || lct.plays.length !== 4) return null;
    const ws = Number(lct.winnerSeat);
    return {
      plays: lct.plays,
      winnerSeat: Number.isInteger(ws) && ws >= 0 && ws <= 3 ? ws : 0,
      completedIdx: Number(lct.trickIndex ?? lct.completedIdx) || 0,
    };
  }, [inPlay, currentTrick.length, lastCompleted, persistedLastTrickStrip]);
  /** Как `showLastTrickRibbon` в `ArmenianBelotTablePresentation` — история последней полной взятки над сукном. */
  const showLastTrickRibbon =
    phase !== 'init' && lastTrickStripDisplay != null;
  const declarationMode = String(belotArmenian?.declarationMode || 'casual');
  const declaredComboKeysBySeat = Array.isArray(belotArmenian?.declaredComboKeysBySeat)
    ? belotArmenian.declaredComboKeysBySeat
    : null;
  const contractMult = Number(contract?.contraMultiplier ?? auction?.contraMultiplier ?? 1);
  const trumpSuit = contract?.trump ?? contract?.trumpSuit ?? null;
  const sortedHand = useMemo(() => sortBelotHandForDisplay(myHand || []), [myHand]);
  const handFanMetrics = useMemo(
    () => computeHandFanMetrics(sortedHand.length || 1, isCompact),
    [sortedHand.length, isCompact],
  );

  const visualFxQueue = useBelotVisualFxQueue({ defaultMinGapMs: 300 });
  const visualFxQueueRef = useRef(visualFxQueue);
  visualFxQueueRef.current = visualFxQueue;

  const enqueueFxRef = useRef(visualFxQueue.enqueue);
  enqueueFxRef.current = visualFxQueue.enqueue;
  useEffect(() => {
    const reg = onRegisterHardcoreSelfPreviewEnqueue;
    if (typeof reg !== 'function') return undefined;
    const api = (item) => enqueueFxRef.current(item);
    reg(api);
    return () => {
      reg(null);
    };
  }, [onRegisterHardcoreSelfPreviewEnqueue]);
  /** Раунды, для которых уже показан публичный ревил хардкора (в т.ч. catch-up). */
  const hardcorePublicRevealDoneRoundsRef = useRef(/** @type {Set<number>} */ (new Set()));
  const onShulerRevealWindowStart = useCallback(
    (ms) => {
      const release = visualFxQueue.acquireCritical();
      window.setTimeout(release, Math.max(0, Number(ms) || 0));
    },
    [visualFxQueue],
  );

  const shulerFx = useBelotShulerCaughtFx({
    belotArmenian,
    belotSyncSeq: Number(belotSyncSeq) || 0,
    play,
    audioSuppressed,
    onRevealWindowStart: onShulerRevealWindowStart,
  });

  const shulerCheaterHandSorted = useMemo(() => {
    const h = shulerFx.caught?.cheaterHand;
    if (!Array.isArray(h) || !h.length) return [];
    return sortBelotHandForDisplay(h);
  }, [shulerFx.caught?.cheaterHand]);

  const roundIndexEngine = Number(belotArmenian?.roundIndex) || 0;

  const declarationStripBattleOutcome = useMemo(() => {
    if (!belotArmenian) return null;
    const dm = String(belotArmenian.declarationMode || 'casual');
    if (dm === 'casual') {
      const evs = Array.isArray(belotArmenian.lastEvents) ? belotArmenian.lastEvents : [];
      for (let i = evs.length - 1; i >= 0; i -= 1) {
        const e = evs[i];
        if (e?.type === 'DECLARATIONS_RESOLVED' && e.payload && typeof e.payload === 'object') {
          const p = e.payload;
          if (p.mode !== 'casual') continue;
          const o = p.outcome;
          if (o === 'team_a' || o === 'team_b' || o === 'tie') return o;
        }
      }
      return null;
    }
    const handsRaw = belotArmenian.handsAtRoundStart ?? belotArmenian.hands;
    if (!hasFullHandsSnapshot(handsRaw)) return null;
    const c = belotArmenian.firstPlayDeclarationsCommitted;
    if (!Array.isArray(c) || c.length !== 4 || !c.every(Boolean)) return null;
    return (
      resolveDeclarationBattleOutcome({
        handsAtRoundStart: handsRaw,
        declaredComboKeysBySeat: belotArmenian.declaredComboKeysBySeat,
        contract: belotArmenian.contract,
      })?.outcome ?? null
    );
  }, [belotArmenian]);

  /** @type {React.MutableRefObject<[number, number]>} */
  const belaRoyalsByTeamRef = useRef(belotFreshBelaRoyalsByTeam());
  const belaSeqBootstrappedRef = useRef(false);
  const lastBelaSeqRef = useRef(0);
  const prevTrickLenComboRef = useRef(0);
  const comboRevealLastKeyRef = useRef('');
  const [belaSpeech, setBelaSpeech] = useState(
    /** @type {null | { seat: number, line1: string, line2: string, token: number, tailUp: boolean, towardCenterPx: number, driftX: number, driftY: number }} */ (
      null
    ),
  );
  const [comboRevealFx, setComboRevealFx] = useState(
    /** @type {null | { token: number, labels: string[], cpFlyText: string, prefersReducedMotion: boolean }} */ (null),
  );

  const prefersReducedMotionFx = useMemo(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
    try {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    } catch {
      return false;
    }
  }, []);

  const [declarationStripBattleApply, setDeclarationStripBattleApply] = useState(false);

  useEffect(() => {
    setDeclarationStripBattleApply(false);
  }, [roundIndexEngine]);

  useEffect(() => {
    if (phase !== 'play') return undefined;
    if (declarationStripBattleOutcome == null) return undefined;
    let cancelled = false;
    void visualFxQueue.waitUntilIdle().then(() => {
      window.setTimeout(
        () => {
          if (!cancelled) setDeclarationStripBattleApply(true);
        },
        prefersReducedMotionFx ? 150 : 450,
      );
    });
    return () => {
      cancelled = true;
    };
  }, [phase, declarationStripBattleOutcome, roundIndexEngine, visualFxQueue, prefersReducedMotionFx]);

  useEffect(() => {
    belaRoyalsByTeamRef.current = belotFreshBelaRoyalsByTeam();
    comboRevealLastKeyRef.current = '';
    hardcorePublicRevealDoneRoundsRef.current.clear();
    visualFxQueueRef.current.clear();
  }, [roundIndexEngine]);

  useEffect(() => {
    const seq = Number(belotSyncSeq) || 0;
    if (!belaSeqBootstrappedRef.current) {
      belaSeqBootstrappedRef.current = true;
      lastBelaSeqRef.current = seq;
      return;
    }
    if (seq <= lastBelaSeqRef.current) return;
    lastBelaSeqRef.current = seq;

    if (phase !== 'play' || !contract) return;
    const trump = contract?.trump ?? contract?.trumpSuit;
    const events = Array.isArray(belotArmenian?.lastEvents) ? belotArmenian.lastEvents : [];
    for (let i = 0; i < events.length; i += 1) {
      const ev = events[i];
      if (!ev || typeof ev !== 'object' || ev.type !== 'CARD_PLAYED') continue;
      const pl = ev.payload && typeof ev.payload === 'object' ? ev.payload : {};
      const seat = Number(pl.seat);
      const cardId = pl.card;
      const trickIndex = Number(pl.trickIndex);
      if (!Number.isInteger(seat) || seat < 0 || seat > 3 || cardId == null) continue;

      const adv = belotAdvanceBelaSpeechState({
        trump,
        cardId,
        seat,
        royalsPlayedByTeam: belaRoyalsByTeamRef.current,
      });
      belaRoyalsByTeamRef.current = adv.royalsPlayedByTeam;
      if (adv.kind !== 'blot' && adv.kind !== 'reblot') continue;

      const line1 = adv.kind === 'blot' ? 'БЛОТ' : 'РЕБЛОТ';
      const glyph = auctionTrumpBubbleGlyph(trump);
      const token = Date.now();
      const place = belotSeatBubblePlacement(heroIndex, seat);

      visualFxQueue.enqueue({
        id: `bela-${seq}-${seat}-${cardId}-${Number.isFinite(trickIndex) ? trickIndex : i}`,
        priority: BELOT_FX_PRIORITY.BELA_SPEECH,
        run: () =>
          new Promise((resolve) => {
            setBelaSpeech({
              seat,
              line1,
              line2: glyph,
              token,
              tailUp: place.tailUp,
              towardCenterPx: place.towardCenterPx,
              driftX: place.driftX,
              driftY: place.driftY,
            });
            window.setTimeout(() => {
              setBelaSpeech((cur) => (cur && cur.token === token ? null : cur));
              resolve();
            }, 1500);
          }),
      });
    }
  }, [belotSyncSeq, belotArmenian, phase, contract, heroIndex, visualFxQueue]);

  useEffect(() => {
    const L = currentTrickLen;
    if (phase !== 'play') {
      prevTrickLenComboRef.current = L;
      return;
    }
    const prev = prevTrickLenComboRef.current;
    prevTrickLenComboRef.current = L;
    if (prev !== 4 || L !== 0) return;

    const playsOk = Array.isArray(lastCompleted?.plays) && lastCompleted.plays.length === 4;
    const trickIdx = Number(lastCompleted?.trickIndex ?? -1);
    const tricksSum =
      (Number(tricksTakenByTeam[0]) || 0) + (Number(tricksTakenByTeam[1]) || 0);
    if (!playsOk || trickIdx < 0 || tricksSum !== 1) return;

    const dedupeKey = `${roundIndexEngine}-${trickIdx}`;
    if (dedupeKey === comboRevealLastKeyRef.current) return;
    comboRevealLastKeyRef.current = dedupeKey;

    const rows = belotArmenian?.declaredComboKeysBySeat;
    const parts = [];
    if (Array.isArray(rows)) {
      for (let s = 0; s < 4; s += 1) {
        const row = rows[s];
        if (Array.isArray(row) && row.length) parts.push(...row.map((k) => String(k)));
      }
    }
    const uniq = [...new Set(parts)].slice(0, 10);
    const lastEv = Array.isArray(belotArmenian?.lastEvents) ? belotArmenian.lastEvents : [];
    let scoringA = 0;
    let scoringB = 0;
    for (let j = lastEv.length - 1; j >= 0; j -= 1) {
      const ev = lastEv[j];
      if (!ev || ev.type !== 'DECLARATIONS_RESOLVED' || !ev.payload || typeof ev.payload !== 'object') {
        continue;
      }
      const p = ev.payload;
      scoringA = Number(p.scoringA);
      scoringB = Number(p.scoringB);
      if (!Number.isFinite(scoringA)) scoringA = 0;
      if (!Number.isFinite(scoringB)) scoringB = 0;
      break;
    }
    const cpTens = Math.round((scoringA + scoringB) / 10);
    const cpFlyText = `+${cpTens} CP`;

    const seq = Number(belotSyncSeq) || 0;
    visualFxQueue.enqueue({
      id: `combo-reveal-${roundIndexEngine}-${trickIdx}-${seq}`,
      priority: BELOT_FX_PRIORITY.COMBO_REVEAL,
      run: () =>
        new Promise((resolve) => {
          const token = Date.now();
          const delayStart = prefersReducedMotionFx ? 0 : 100;
          window.setTimeout(() => {
            setComboRevealFx({
              token,
              labels: uniq.length ? uniq : ['Объявления'],
              cpFlyText,
              prefersReducedMotion: prefersReducedMotionFx,
            });
            const holdMs = prefersReducedMotionFx ? 650 : 2000;
            window.setTimeout(() => {
              setComboRevealFx((c) => (c && c.token === token ? null : c));
              resolve();
            }, holdMs);
          }, delayStart);
        }),
    });
  }, [
    belotArmenian,
    belotSyncSeq,
    currentTrickLen,
    lastCompleted,
    phase,
    prefersReducedMotionFx,
    roundIndexEngine,
    tricksTakenByTeam,
    visualFxQueue,
  ]);

  useEffect(() => {
    if (!belaSpeech) return undefined;
    let cancelled = false;
    let ctx = null;
    const rafId = window.requestAnimationFrame(() => {
      if (cancelled) return;
      const el = document.querySelector(`[data-bela-bubble-root="${String(belaSpeech.token)}"]`);
      if (!el || cancelled) return;
      const { driftX, driftY } = belaSpeech;
      if (prefersReducedMotionFx) {
        ctx = gsap.context(() => {
          gsap.fromTo(el, { opacity: 0 }, { opacity: 1, duration: 0.12, ease: 'none' });
          gsap.to(el, { opacity: 0, delay: 0.55, duration: 0.2, ease: 'none' });
        }, el);
        return;
      }
      ctx = gsap.context(() => {
        gsap.fromTo(
          el,
          { scale: 0, opacity: 0, xPercent: -50, x: 0, y: 0 },
          { scale: 1, opacity: 1, xPercent: -50, x: 0, y: 0, duration: 0.22, ease: 'back.out(1.7)' },
        );
        gsap.to(el, {
          xPercent: -50,
          x: driftX,
          y: driftY,
          duration: 0.75,
          delay: 0.2,
          ease: 'power1.out',
        });
        gsap.to(el, {
          xPercent: -50,
          x: driftX,
          y: driftY,
          opacity: 0,
          scale: 0.9,
          delay: 1.05,
          duration: 0.42,
          ease: 'power2.in',
        });
      }, el);
    });
    return () => {
      cancelled = true;
      window.cancelAnimationFrame(rafId);
      if (ctx) ctx.revert();
    };
  }, [belaSpeech, prefersReducedMotionFx]);

  const seatNodeRefs = useRef({});
  const prePlayLaunchAnchorRef = useRef(/** @type {HTMLElement|null} */ (null));
  /** @type {React.MutableRefObject<Set<string>>} */
  const casualDeclareComboRevealDoneRef = useRef(new Set());
  /** То же ref (старое имя) — избегает ReferenceError при частичном HMR после переименования. */
  // eslint-disable-next-line no-unused-vars -- имя может вызываться из устаревшего hot-update чанка
  const casualDeclareTerzRevealDoneRef = casualDeclareComboRevealDoneRef;

  useEffect(() => {
    casualDeclareComboRevealDoneRef.current = new Set();
  }, [roundIndexEngine]);

  useEffect(() => {
    if (typeof runQueuedComboCardReveal !== 'function') return undefined;
    if (phase !== 'declare' || declarationMode !== 'casual') return undefined;
    const rows = Array.isArray(declaredComboKeysBySeat) ? declaredComboKeysBySeat : [];
    const r = roundIndexEngine;
    for (let seat = 0; seat < 4; seat += 1) {
      const keys = rows[seat];
      if (!Array.isArray(keys) || !keys.length) continue;
      for (const rawKey of keys) {
        const { label, labelRaw, cardIds } = parseDeclaredComboKey(rawKey);
        if (!hasRevealableDeclaredComboCardIds(cardIds)) continue;
        const dedupe = `casual-declare-combo-${r}-seat${seat}-${String(rawKey).replace(/\s+/g, '').slice(0, 80)}`;
        if (casualDeclareComboRevealDoneRef.current.has(dedupe)) continue;
        casualDeclareComboRevealDoneRef.current.add(dedupe);
        visualFxQueue.enqueue({
          id: dedupe,
          priority: BELOT_FX_PRIORITY.PRE_PLAY_COMBO_REVEAL,
          run: () => {
            prePlayLaunchAnchorRef.current = seatNodeRefs.current[seat] ?? null;
            return runQueuedComboCardReveal({
              cardIds,
              label: label || undefined,
              comboLabelRaw: labelRaw || undefined,
              launchAnchorRef: prePlayLaunchAnchorRef,
              timingPreset: 'normal',
            });
          },
        });
      }
    }
    return undefined;
  }, [
    runQueuedComboCardReveal,
    phase,
    declarationMode,
    declaredComboKeysBySeat,
    roundIndexEngine,
    visualFxQueue,
  ]);

  useEffect(() => {
    if (typeof runQueuedComboCardReveal !== 'function') return undefined;
    if (phase !== 'play' || declarationMode !== 'hardcore') return undefined;
    const c = belotArmenian?.firstPlayDeclarationsCommitted;
    if (!Array.isArray(c) || c.length !== 4 || !c.every(Boolean)) return undefined;
    const r = roundIndexEngine;
    if (hardcorePublicRevealDoneRoundsRef.current.has(r)) return undefined;
    const rows = Array.isArray(declaredComboKeysBySeat) ? declaredComboKeysBySeat : [];
    let hasAny = false;
    for (let s = 0; s < 4; s += 1) {
      if (rows[s]?.length) {
        hasAny = true;
        break;
      }
    }
    if (!hasAny) {
      hardcorePublicRevealDoneRoundsRef.current.add(r);
      return undefined;
    }
    const isCatchup = tricksTakenTotal > 0 || currentTrickLen > 0;
    const timingPreset = isCatchup ? 'catchup' : 'normal';
    const queueItem = isCatchup ? visualFxQueue.prepend.bind(visualFxQueue) : visualFxQueue.enqueue.bind(visualFxQueue);

    hardcorePublicRevealDoneRoundsRef.current.add(r);
    const seatOrder = [0, 2, 1, 3];
    for (const seat of seatOrder) {
      const keys = rows[seat];
      if (!Array.isArray(keys) || !keys.length) continue;
      const keysSorted = sortDeclaredComboKeysForRevealDescending(keys);
      for (let ki = 0; ki < keysSorted.length; ki += 1) {
        const rawKey = keysSorted[ki];
        const { label, labelRaw, cardIds } = parseDeclaredComboKey(rawKey);
        const ids = cardIds.length ? cardIds : [-1];
        const safeKey = String(rawKey).replace(/\s+/g, '').slice(0, 80);
        queueItem({
          id: `preplay-hardcore-${r}-seat${seat}-${safeKey}-${ki}`,
          priority: BELOT_FX_PRIORITY.PRE_PLAY_COMBO_REVEAL,
          run: () => {
            prePlayLaunchAnchorRef.current = seatNodeRefs.current[seat] ?? null;
            return runQueuedComboCardReveal({
              cardIds: ids,
              label: label || undefined,
              comboLabelRaw: labelRaw || undefined,
              launchAnchorRef: prePlayLaunchAnchorRef,
              timingPreset,
            });
          },
        });
      }
    }
    return undefined;
  }, [
    runQueuedComboCardReveal,
    belotArmenian,
    phase,
    declarationMode,
    declaredComboKeysBySeat,
    tricksTakenTotal,
    currentTrickLen,
    roundIndexEngine,
    visualFxQueue,
  ]);

  const prevAuctionRef = useRef({
    phase: phase,
    turnSeat,
    highestBidKey: null,
    contraMultiplier: contractMult,
    lastCompletedTrickIdx: Number(lastCompleted?.trickIndex ?? -1),
    currentTrickLen: currentTrick.length,
  });
  const turnStartedAtRef = useRef(Date.now());
  const [seatActions, setSeatActions] = useState({});
  const [auctionSeatActions, setAuctionSeatActions] = useState({});
  const [botTurnNowMs, setBotTurnNowMs] = useState(Date.now());
  const [auctionLogOpen, setAuctionLogOpen] = useState(false);
  const [auctionLog, setAuctionLog] = useState([]);
  const showTableAuctionRiver =
    inAuction && Array.isArray(auctionLog) && auctionLog.length > 0;
  const [hoveredCardIndex, setHoveredCardIndex] = useState(null);
  const [pendingHiddenCardId, setPendingHiddenCardId] = useState(null);
  const [cardFly, setCardFly] = useState(null);
  /** @type {[null | { plays: { seat: number, card: number }[], winnerSeat: number|null, token: number }, (v: any) => void]} */
  const [trickEndOverlay, setTrickEndOverlay] = useState(null);
  const flyCardWrapRef = useRef(null);
  const flyTweenRef = useRef(null);
  const pendingPayloadRef = useRef(null);
  const playFlightLockRef = useRef(false);
  const [roundDealAnimActive, setRoundDealAnimActive] = useState(false);
  const [seatDealSlotRevealed, setSeatDealSlotRevealed] = useState(null);
  const setRoundDealAnimActiveWrapped = useCallback(
    (v) => {
      setRoundDealAnimActive(v);
      onRoundDealAnimActiveChange?.(v);
    },
    [onRoundDealAnimActiveChange],
  );
  const lastTrickLongPressRef = useRef(null);
  const lastTrickLongPressTriggeredRef = useRef(false);
  const handSlotRefs = useMemo(() => Array.from({ length: 8 }, () => React.createRef()), []);

  const prefersReducedMotionDeckTrump = useMemo(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
    try {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    } catch {
      return false;
    }
  }, []);

  useEffect(() => {
    const now = Date.now();
    const patchRegular = {};
    const patchAuction = {};
    const clearExpired = (src) => {
      const next = { ...src };
      for (const k of Object.keys(next)) {
        if (!next[k]?.at || now - next[k].at > SEAT_ACTION_TTL_MS) {
          delete next[k];
        }
      }
      return next;
    };
    const currentHighestBid = auction?.highestBid;
    const highestBidKey = currentHighestBid
      ? `${Number(currentHighestBid.tens || 0)}:${String(currentHighestBid.trump)}:${Boolean(currentHighestBid.kapot)}`
      : null;
    const prev = prevAuctionRef.current;

    if (phase !== prev.phase || turnSeat !== prev.turnSeat) {
      turnStartedAtRef.current = now;
    }

    if (phase === 'auction' && prev.phase === 'auction' && prev.turnSeat != null && turnSeat !== prev.turnSeat) {
      const actedSeat = prev.turnSeat;
      if (highestBidKey && highestBidKey !== prev.highestBidKey && auction?.bidAuthorSeat === actedSeat) {
        const bidCp = bidCpTensForDisplay(currentHighestBid);
        const bidTrump = formatBelotTrumpBid(currentHighestBid?.trump);
        patchAuction[actedSeat] = {
          kind: 'bid',
          label: bidCp !== '—' ? `Бид ${bidCp} CP ${bidTrump}` : `Бид ${bidTrump}`,
          at: now,
          bid: currentHighestBid,
        };
      } else if (contractMult > prev.contraMultiplier) {
        patchAuction[actedSeat] = {
          kind: contractMult >= 4 ? 'recontra' : 'contra',
          label: contractMult >= 4 ? 'Реконтра' : 'Контра',
          at: now,
        };
      } else {
        patchAuction[actedSeat] = { kind: 'pass', label: 'Пас', at: now };
      }
    }

    if (phase === 'play' && currentTrick.length > prev.currentTrickLen) {
      const lastPlay = currentTrick[currentTrick.length - 1];
      if (Number.isInteger(lastPlay?.seat)) {
        patchRegular[lastPlay.seat] = { kind: 'play', label: 'Ход', at: now };
      }
    }
    const completedTrickIdx = Number(lastCompleted?.trickIndex ?? -1);
    if (
      phase === 'play' &&
      prev.currentTrickLen >= 3 &&
      currentTrick.length === 0 &&
      completedTrickIdx !== prev.lastCompletedTrickIdx &&
      Number.isInteger(lastCompleted?.winnerSeat)
    ) {
      patchRegular[lastCompleted.winnerSeat] = { kind: 'trick', label: 'Взял взятку', at: now };
    }

    setSeatActions((prevMap) => ({ ...clearExpired(prevMap), ...patchRegular }));
    if (phase === 'auction') {
      setAuctionSeatActions((prevMap) => ({ ...clearExpired(prevMap), ...patchAuction }));
      const patchedSeats = Object.keys(patchAuction);
      if (patchedSeats.length > 0) {
        setAuctionLog((prevLog) =>
          [
            ...prevLog,
            ...patchedSeats.map((seatKey, i) => {
              const seat = Number(seatKey);
              const row = patchAuction[seat];
              return {
                id: `${now}-${seat}-${row.kind}-${i}-${prevLog.length}`,
                at: now,
                seat,
                kind: row.kind,
                label: row.label,
              };
            }),
          ].slice(-36),
        );
      }
    } else {
      setAuctionSeatActions({});
      setAuctionLogOpen(false);
      setAuctionLog([]);
    }
    prevAuctionRef.current = {
      phase,
      turnSeat,
      highestBidKey,
      contraMultiplier: contractMult,
      currentTrickLen: currentTrick.length,
      lastCompletedTrickIdx: completedTrickIdx,
    };
  }, [
    phase,
    turnSeat,
    auction,
    contractMult,
    currentTrick,
    lastCompleted,
  ]);

  useEffect(() => {
    if (!(inAuction || inPlay) || turnSeat == null || turnSeat === heroIndex) return undefined;
    const id = window.setInterval(() => setBotTurnNowMs(Date.now()), 180);
    return () => window.clearInterval(id);
  }, [inAuction, inPlay, turnSeat, heroIndex]);

  useEffect(() => {
    if (pendingHiddenCardId == null) return;
    if (!Array.isArray(myHand) || !myHand.includes(pendingHiddenCardId)) {
      setPendingHiddenCardId(null);
    }
  }, [myHand, pendingHiddenCardId]);

  useEffect(() => {
    if (!auctionLogOpen) return undefined;
    const onKeyDown = (ev) => {
      if (ev.key === 'Escape') setAuctionLogOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [auctionLogOpen]);

  useEffect(
    () => () => {
      if (lastTrickLongPressRef.current != null) {
        window.clearTimeout(lastTrickLongPressRef.current);
        lastTrickLongPressRef.current = null;
      }
    },
    [],
  );

  const clearLastTrickLongPress = () => {
    if (lastTrickLongPressRef.current != null) {
      window.clearTimeout(lastTrickLongPressRef.current);
      lastTrickLongPressRef.current = null;
    }
  };

  useBelotOnlineDealAnimation({
    belotArmenian,
    deckRef,
    seatRefs: seatNodeRefs,
    handSlotRefs,
    seatCardSlotRefs: undefined,
    play,
    audioSuppressed,
    suppressDealAnimation,
    cardsPerSeatForDeal: 8,
    heroSeat: heroIndex,
    heroHand: myHand,
    setRoundDealAnimActive: setRoundDealAnimActiveWrapped,
    setSeatDealSlotRevealed,
    onDealFxLock: () => visualFxQueueRef.current.acquireCritical(),
  });

  const commitHeroPlay = useCallback(
    (/** @type {{ card: number, from: 'hand' }} */ payload, dragDropFromPoint) => {
      if (phase !== 'play' || !canPlay) return;
      if (playFlightLockRef.current) return;
      const cardId = payload.card;
      const trickEl = trickCenterRef?.current;
      const reduced =
        typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
      if (!trickEl || reduced) {
        onPlayCard?.(cardId);
        return;
      }
      const i = sortedHand.indexOf(cardId);
      const fromEl = i >= 0 ? handSlotRefs[i]?.current : null;
      if (!fromEl) {
        onPlayCard?.(cardId);
        return;
      }
      const { rank, suit } = belotCardIdToPlaying(cardId);
      const fr = fromEl.getBoundingClientRect();
      const tr = trickEl.getBoundingClientRect();
      playFlightLockRef.current = true;
      onPlayFlightActiveChange?.(true);
      pendingPayloadRef.current = payload;
      setPendingHiddenCardId(cardId);
      const startX = dragDropFromPoint?.x ?? fr.left + fr.width / 2;
      const startY = dragDropFromPoint?.y ?? fr.top + fr.height / 2;
      setCardFly({
        key: Date.now(),
        x0: startX,
        y0: startY,
        x1: tr.left + tr.width / 2,
        y1: tr.top + tr.height / 2,
        rank,
        suit,
        fromDrag: Boolean(dragDropFromPoint),
      });
    },
    [phase, canPlay, onPlayCard, sortedHand, handSlotRefs, trickCenterRef, onPlayFlightActiveChange],
  );

  const waitUntilVisualFxIdle = useCallback(() => visualFxQueueRef.current.waitUntilIdle(), []);

  useBelotOnlinePlayCardFlight({
    cardFly,
    setCardFly,
    flyCardWrapRef,
    flyTweenRef,
    trickCenterRef,
    pendingPayloadRef,
    onSend: (p) => onPlayCard?.(p.card),
    onFlightEnd: () => {
      playFlightLockRef.current = false;
      onPlayFlightActiveChange?.(false);
    },
    waitUntilFxIdle: waitUntilVisualFxIdle,
  });

  const {
    dragPlayCard,
    dragGhostRef,
    onPlayPointerDown,
    onPlayPointerMove,
    onPlayPointerUp,
    onPlayPointerCancel,
    onPlayCardClick,
    clearDragPlayCard,
  } = useBelotOnlineHeroDragPlay({
    trickCenterRef,
    enabled: phase === 'play' && !roundDealAnimActive,
    isInputBlocked: !canPlay || Boolean(cardFly),
    cardIdToPlaying: belotCardIdToPlaying,
    onCommitPlay: commitHeroPlay,
  });

  const belotTrickRevealMsWhen = useCallback(({ tricksTakenTotal: tot }) => {
    return tot === 1 ? 2150 : undefined;
  }, []);

  const belotTrickFxQueueConfig = useMemo(
    () => ({
      enqueue: visualFxQueue.enqueue,
      trickEndPriority: BELOT_FX_PRIORITY.TRICK_END_OVERLAY,
    }),
    [visualFxQueue.enqueue],
  );

  useBelotOnlineTrickEndAnimation({
    belotPhase: phase,
    roundIndex: Number(belotArmenian?.roundIndex) || 0,
    currentTrickLen,
    lastCompletedTrick: lastCompleted,
    setTrickEndOverlay,
    revealMsWhen: belotTrickRevealMsWhen,
    tricksTakenTotal,
    fxQueue: belotTrickFxQueueConfig,
  });

  useEffect(() => {
    flyTweenRef.current?.kill();
    flyTweenRef.current = null;
    pendingPayloadRef.current = null;
    setCardFly(null);
    setPendingHiddenCardId(null);
    playFlightLockRef.current = false;
    clearDragPlayCard();
    onPlayFlightActiveChange?.(false);
  }, [belotPlayResyncKey, clearDragPlayCard, onPlayFlightActiveChange]);

  const showLastTrickButton =
    inPlay &&
    currentTrick.length === 0 &&
    Array.isArray(lastCompleted?.plays) &&
    lastCompleted.plays.length === 4;

  const seatsForRender = useMemo(() => {
    return [0, 1, 2, 3].map((seatIndex) => {
      const row = belotSeatRows.find((r) => r.seat === seatIndex);
      const visualOffset = (seatIndex - heroIndex + 4) % 4;
      const slot = (isCompact ? SEAT_MOBILE_STACK_PRESETS : SEAT_ELLIPSE_PRESETS)[visualOffset];
      const isHero = seatIndex === heroIndex;
      return {
        seatIndex,
        slot,
        row,
        isHero,
      };
    });
  }, [belotSeatRows, heroIndex, isCompact]);

  return (
    <div className="relative flex w-full flex-col gap-1 sm:gap-2 md:gap-3">
      {(inAuction || (contract && (phase === 'declare' || phase === 'play'))) ? (
        <div className="sticky top-0 z-[25] shrink-0 border-b border-border/50 bg-card/88 px-2 py-1.5 shadow-sm backdrop-blur-md sm:px-3 sm:py-2">
          <div className="mx-auto flex max-w-2xl items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <p className="text-[9px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                {inAuction ? 'Текущий контракт' : 'Контракт'}
              </p>
              <p className="mt-0.5 truncate text-xs font-medium leading-snug text-foreground sm:text-[13px]">
                {inAuction && auction?.highestBid ? (
                  <>
                    <span className="font-black text-amber-100">Mazl:</span>{' '}
                    <span className="font-mono text-amber-200/95">
                      {bidCpTensForDisplay(auction.highestBid)} CP
                    </span>{' '}
                    <span>{formatBelotTrumpBid(auction.highestBid.trump)}</span>
                    {Number(auction.contraMultiplier) > 1 ? (
                      <span className="ml-1 font-mono text-xs text-cyan-300/90">
                        ×{Number(auction.contraMultiplier)}
                      </span>
                    ) : null}
                  </>
                ) : inAuction ? (
                  <span className="text-muted-foreground">Открытие от 10 CP</span>
                ) : contract ? (
                  <>
                    <span className="font-mono tabular-nums text-amber-200/95">
                      {belotContractCpLabelFromEngine(contract)} CP
                    </span>
                    <span className="text-muted-foreground/80"> · </span>
                    <span>{formatBelotTrumpBid(contract.trump)}</span>
                    {contract.kapot ? (
                      <>
                        <span className="text-muted-foreground/80"> · </span>
                        <span className="rounded border border-amber-500/35 bg-amber-950/20 px-1 py-px text-[10px] font-semibold uppercase text-amber-100/90">
                          капот
                        </span>
                      </>
                    ) : null}
                    {contractMult > 1 ? (
                      <span className="ml-1 font-mono text-xs text-cyan-300/90">×{contractMult}</span>
                    ) : null}
                  </>
                ) : null}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {(() => {
                const trump = inAuction ? auction?.highestBid?.trump : contract?.trump;
                const L = trump != null ? largeTrumpDisplay(trump) : { line1: '—', line2: null };
                return (
                  <div
                    ref={contractBadgeAnchorRef}
                    className={cn(
                      'flex h-10 w-10 shrink-0 items-center justify-center rounded-md border bg-black/30 text-xl font-bold leading-none shadow-inner sm:h-11 sm:w-11 sm:text-2xl',
                      trumpBadgeToneByGlyph(L.line1),
                    )}
                    title={L.line2 || undefined}
                    aria-hidden
                  >
                    <span className={belotTrumpGlyphFaceClass(L.line1)}>{L.line1}</span>
                  </div>
                );
              })()}
              {inAuction ? (
                <button
                  type="button"
                  onClick={() => setAuctionLogOpen((v) => !v)}
                  className="rounded-md border border-border/55 bg-transparent px-2 py-1 text-[10px] font-medium text-muted-foreground transition hover:bg-muted/20 hover:text-foreground sm:text-[11px]"
                >
                  Лог торгов
                </button>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}

      <div className="mx-auto flex w-full max-w-2xl flex-wrap items-center gap-x-2 gap-y-0.5 border-b border-border/35 px-1 pb-1.5 text-[11px] text-muted-foreground sm:gap-x-3 sm:px-1.5 sm:pb-2 sm:text-xs">
        <span className="tabular-nums">
          <span className="text-muted-foreground/75">Счёт</span>{' '}
          <span className="font-mono font-semibold text-foreground">
            {belotMatchCpFromEnginePoints(teamScores[0])} : {belotMatchCpFromEnginePoints(teamScores[1])}
          </span>
          {targetScore ? (
            <span className="text-muted-foreground/65">
              {' '}
              / {belotMatchTargetCpFromEngine(targetScore)} CP
            </span>
          ) : null}
        </span>
        <span className="text-muted-foreground/35">·</span>
        <span className="tabular-nums">
          <span className="text-muted-foreground/75">Взятки</span>{' '}
          <span className="font-mono font-semibold text-foreground">
            {tricksTakenByTeam[0]} : {tricksTakenByTeam[1]}
          </span>
        </span>
        <span className="text-muted-foreground/35">·</span>
        <span className="tabular-nums text-muted-foreground/90">
          Раунд{' '}
          <span className="font-mono font-medium text-foreground">
            {(belotArmenian?.roundIndex ?? 0) + 1}
          </span>
        </span>
        <span className="ml-auto flex flex-wrap items-center gap-1.5">
          {onOpenScoreSheet ? (
            <button
              type="button"
              onClick={onOpenScoreSheet}
              className="rounded-md border border-border/55 bg-transparent px-2 py-1 text-[10px] font-medium text-muted-foreground transition hover:bg-muted/20 hover:text-foreground sm:text-[11px]"
            >
              <Braces className="mr-1 inline h-3 w-3 -translate-y-px shrink-0 opacity-80" aria-hidden />
              Листок
            </button>
          ) : null}
          {showLastTrickButton && onPeekLastTrick ? (
            <button
              type="button"
              onClick={() => {
                if (lastTrickLongPressTriggeredRef.current) {
                  lastTrickLongPressTriggeredRef.current = false;
                  return;
                }
                onPeekLastTrick();
              }}
              onPointerDown={(e) => {
                if (e.button !== 0) return;
                lastTrickLongPressTriggeredRef.current = false;
                clearLastTrickLongPress();
                lastTrickLongPressRef.current = window.setTimeout(() => {
                  lastTrickLongPressRef.current = null;
                  lastTrickLongPressTriggeredRef.current = true;
                  onPeekLastTrick();
                }, 450);
              }}
              onPointerUp={clearLastTrickLongPress}
              onPointerLeave={clearLastTrickLongPress}
              onPointerCancel={clearLastTrickLongPress}
              className="inline-flex items-center gap-1 rounded-md border border-border/55 bg-transparent px-2 py-1 text-[10px] font-medium text-muted-foreground transition hover:bg-muted/20 hover:text-foreground sm:text-[11px]"
            >
              <Eye className="h-3 w-3 shrink-0 opacity-80" aria-hidden />
              Взятка
            </button>
          ) : null}
          {heroSecLeft != null && turnSeat === heroIndex && (inAuction || inPlay) ? (
            <span className="inline-flex items-center gap-1 rounded-md border border-neon-cyan/35 bg-neon-cyan/10 px-2 py-1 font-mono text-[10px] tabular-nums text-neon-cyan sm:text-[11px]">
              <Timer className="h-3 w-3 shrink-0 opacity-90" aria-hidden />
              {heroSecLeft}s
            </span>
          ) : null}
        </span>
      </div>

      {showLastTrickRibbon ? (
        <div
          className="relative z-[26] ml-0 w-max max-w-full shrink-0 self-start px-1 sm:px-1.5"
          aria-live="polite"
          aria-label="Последняя завершённая взятка"
        >
          <div
            key={`lt-online-${lastTrickStripDisplay.completedIdx}:${lastTrickStripDisplay.plays.map((p) => `${p.seat}:${p.card}`).join('|')}`}
            className={cn(
              'w-max max-w-full overflow-x-auto rounded-xl border border-neon-gold/45 bg-black/65 px-2 py-1 shadow-[0_10px_36px_rgba(0,0,0,0.55),inset_0_1px_0_rgba(255,255,255,0.08)] backdrop-blur-md sm:px-2.5 sm:py-1.5',
              !prefersReducedMotionFx &&
                'motion-safe:animate-[app-toast-in_420ms_cubic-bezier(0.2,0.8,0.2,1)]',
            )}
          >
            <p className="whitespace-nowrap text-left text-[10px] font-black uppercase tracking-wide text-neon-gold [text-shadow:0_1px_3px_rgba(0,0,0,0.85)]">
              Последняя взятка · №{lastTrickStripDisplay.completedIdx || '—'}
            </p>
            <p className="mt-0.5 whitespace-nowrap text-left text-[9px] font-semibold text-white/90 [text-shadow:0_1px_2px_rgba(0,0,0,0.88)]">
              Забрал:{' '}
              {seatDisplayNameForOnline(
                lastTrickStripDisplay.winnerSeat,
                heroIndex,
                heroName,
                belotSeatRows,
              )}
            </p>
            <div className="mt-1 flex flex-nowrap justify-start gap-1 py-0.5 pb-1">
              {playsToOnlineTrickUiRows(
                lastTrickStripDisplay.plays,
                heroIndex,
                heroName,
                belotSeatRows,
              ).map((c) => {
                const isHeroPlay = mySeat != null && c.seat === mySeat;
                return (
                  <div
                    key={`lt-flow-online-${lastTrickStripDisplay.completedIdx}-${c.key}`}
                    className="flex shrink-0 flex-col items-center gap-px"
                  >
                    <div
                      className={cn(
                        'rounded-[5px]',
                        isHeroPlay && 'p-[2px] ring-2 ring-cyan-300/95 ring-offset-0',
                      )}
                    >
                      <PlayingCard
                        rank={c.rank}
                        suit={c.suit}
                        size="xxs"
                        className={cn(
                          'border border-white/25 shadow-md shadow-black/60',
                          c.dirty && 'ring-2 ring-amber-400/80',
                        )}
                      />
                    </div>
                    <span className="max-w-[4.25rem] truncate text-[8.5px] font-semibold leading-tight text-white/95 [text-shadow:0_1px_2px_rgba(0,0,0,0.9)]">
                      {c.label}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      ) : null}

      {statusBanner && !(inAuction && roundDealAnimActive) ? (
        <div
          className={cn(
            'mx-auto w-full max-w-2xl rounded-xl border px-2 py-1.5 text-center text-xs font-semibold shadow-inner sm:px-3 sm:py-2',
            inAuction
              ? 'border-amber-500/40 bg-amber-950/25 text-amber-100'
              : inPlay
                ? heroSecLeft != null && turnSeat === heroIndex
                  ? 'border-neon-cyan/35 bg-card/70 text-neon-cyan'
                  : 'border-border/60 bg-card/60 text-muted-foreground'
                : 'border-neon-gold/30 bg-card/70 text-neon-gold',
          )}
        >
          {statusBanner}
        </div>
      ) : null}

      <div
        className={cn(
          'relative mx-auto w-full',
          isCompact
            ? 'max-w-[min(100%,23rem)] min-h-[268px] sm:min-h-[340px] md:min-h-[360px]'
            : 'max-w-[min(100%,26rem)] min-h-[288px] sm:min-h-[360px] md:min-h-[410px] lg:min-h-[470px]',
        )}
      >
        <div className="absolute inset-0 flex items-center justify-center py-0.5">
          <div className="relative w-full max-w-lg md:max-w-xl">
            <PokerTableSurface
              ref={deckRef}
              communitySlots={[null, null, null, null, null]}
              pot={tricksTakenByTeam[0] + tricksTakenByTeam[1] || 0}
              potLabel="ВЗЯТКИ"
              showCommunity={false}
              showDeck
              deckPulseKey={belotArmenian?.roundIndex ?? 0}
              deckLayerExtra={
                <BelotDeckTrumpIndicator
                  enabled
                  phase={phase}
                  contract={contract}
                  roundIndex={Number(belotArmenian?.roundIndex) || 0}
                  auctionRevealMs={2350}
                  prefersReducedMotion={prefersReducedMotionDeckTrump}
                  cardIdToPlaying={belotCardIdToPlaying}
                />
              }
            />
          </div>
        </div>
        <div className="pointer-events-none absolute inset-0" style={{ zIndex: BELOT_FX_Z_TABLE }}>
          {seatsForRender.map(({ seatIndex, slot, row, isHero }) => {
            const isCurrentTurn =
              (inAuction || inDeclare || inPlay) && turnSeat === seatIndex;
            const tricksForTeam = Number(tricksTakenByTeam[teamOfSeat(seatIndex)] ?? 0);
            const seatName = row?.name || (row?.uid ? `id …${row.uid.slice(-4)}` : '—');
            const casualDeclareHand =
              phase === 'declare' &&
              declarationMode !== 'hardcore' &&
              isHero &&
              Array.isArray(belotArmenian?.hands?.[seatIndex]) &&
              belotArmenian.hands[seatIndex].length === 8
                ? belotArmenian.hands[seatIndex]
                : null;
            const auctionSeatAction = auctionSeatActions?.[seatIndex];
            const regularSeatAction = seatActions?.[seatIndex];
            const seatAction = inAuction ? auctionSeatAction : regularSeatAction;
            const isAuctionPassed = inAuction && auctionSeatAction?.kind === 'pass';
            const elapsed = Math.max(0, botTurnNowMs - turnStartedAtRef.current);
            const botTurnFraction =
              isCurrentTurn && !isHero && (inAuction || inDeclare || inPlay)
                ? Math.max(0, Math.min(1, 1 - elapsed / (heroTurnSeconds * 1000)))
                : undefined;
            const showAuctionSpeech =
              inAuction &&
              seatAction?.kind &&
              ['pass', 'bid', 'contra', 'recontra'].includes(seatAction.kind) &&
              (seatAction.kind !== 'bid' || seatAction.bid);
            const nameActionBadge =
              !inAuction && seatAction?.kind && !showAuctionSpeech ? (
                <SeatLastMoveBadge
                  key={`online-seat-action-${seatIndex}-${seatAction.at || seatAction.kind}`}
                  action={seatAction}
                  compact={isCompact}
                />
              ) : null;
            const isShulerCaughtSeat =
              shulerFx.caught != null && shulerFx.caught.cheaterSeat === seatIndex;
            const stripKeys = declaredComboKeysBySeat?.[seatIndex] ?? null;
            const phaseMatch = inDeclare || inPlay;
            const seatTeamIx = teamOfSeat(seatIndex);
            const declarePills = collectDeclarePillLabels(
              stripKeys,
              phase,
              declarationMode,
              casualDeclareHand,
              {
                battleOutcome: declarationStripBattleOutcome,
                battleApply: declarationStripBattleApply,
                seatTeamIndex: seatTeamIx,
              },
            );
            const comboJoined =
              declarePills.length > 0
                ? declarePills.slice(0, 4).join(' · ') + (declarePills.length > 4 ? '…' : '')
                : '';
            const matchDropLabel = comboJoined || '';
            const seatMatchDropBadge =
              !inAuction && phaseMatch && matchDropLabel ? (
                <SeatBelotMatchDropBadge
                  key={`online-match-drop-${seatIndex}-${belaSpeech?.token ?? 'x'}-${matchDropLabel}`}
                  label={matchDropLabel}
                  isCurrentTurn={isCurrentTurn}
                  variant="combo"
                />
              ) : null;
            const belaHere =
              belaSpeech && belaSpeech.seat === seatIndex ? (
                <div
                  data-bela-bubble-root={belaSpeech.token}
                  className={cn(
                    'absolute left-1/2 -translate-x-1/2',
                    belaSpeech.tailUp ? 'top-full mt-px' : 'bottom-full mb-px',
                  )}
                  style={{
                    zIndex: BELOT_FX_Z_SPEECH_BUBBLE,
                    marginLeft: belaSpeech.towardCenterPx,
                  }}
                >
                  <BelotFxBubble
                    variant="speech"
                    tail={belaSpeech.tailUp ? 'up' : 'down'}
                    speechTonePair={belotSpeechTonePairFromGlyphLine(belaSpeech.line2)}
                  >
                    <div className="flex flex-col items-center gap-px px-0.5">
                      <span
                        className={cn(
                          'select-none text-[10px] font-black uppercase tracking-wide leading-tight',
                          BELOT_FELT_LABEL_NEUTRAL,
                        )}
                      >
                        {belaSpeech.line1}
                      </span>
                      <div className="select-none text-[10px] font-black leading-none">
                        <BelotVividSuitInlineText
                          text={belaSpeech.line2}
                          neutralClassName={cn(BELOT_FELT_LABEL_NEUTRAL, 'text-[10px] font-black')}
                        />
                      </div>
                    </div>
                  </BelotFxBubble>
                </div>
              ) : null;
            return (
              <div
                key={`online-seat-${seatIndex}`}
                className="absolute"
                style={{
                  left: `${slot.leftPct}%`,
                  top: `${slot.topPct}%`,
                  transform: 'translate(-50%, -50%)',
                  transformOrigin: 'center',
                }}
              >
                <div
                  ref={(el) => {
                    seatNodeRefs.current[seatIndex] = el;
                    if (seatIndex === heroIndex && heroSeatAnchorRef) {
                      if (typeof heroSeatAnchorRef === 'function') heroSeatAnchorRef(el);
                      else heroSeatAnchorRef.current = el;
                    }
                  }}
                  className={cn(
                    'relative flex w-max max-w-[100vw] flex-col items-center gap-0.5 transition-all duration-250',
                    'pointer-events-none',
                    isAuctionPassed && 'opacity-60 saturate-75',
                    isShulerCaughtSeat &&
                      'is-caught-cheater rounded-xl ring-2 ring-red-500/80 shadow-[0_0_28px_rgba(239,68,68,0.55)]',
                    isShulerCaughtSeat &&
                      shulerFx.animated &&
                      'motion-safe:animate-[poker-loss-shake_0.45s_ease-in-out_2] motion-reduce:animate-none',
                  )}
                >
                  {showAuctionSpeech ? (
                    <BelotAuctionSeatActionBubble action={seatAction} className="order-first" />
                  ) : null}
                  {belaHere}
                  <IndustrialSeat
                    name={seatName.length > 10 ? `${seatName.slice(0, 9)}…` : seatName}
                    stackMetaVertically
                    boostSeatAvatar
                    chipsDisplay={null}
                    chips={null}
                    seatColorIndex={seatIndex}
                    isCurrentTurn={isCurrentTurn}
                    isUser={isHero}
                    isBot={!isHero}
                    avatarUrl={row?.avatarUrl || ''}
                    isDealer={dealerSeat === seatIndex}
                    showCards={false}
                    cards={[]}
                    turnTimeLeftSec={isHero && isCurrentTurn ? heroSecLeft : null}
                    turnTimeTotalSec={isHero && isCurrentTurn ? heroTurnSeconds : null}
                    turnTimeFraction={botTurnFraction}
                    modeIndicatorSlot={seatMatchDropBadge}
                    footerLabel={tricksForTeam > 0 ? `🃏 ${tricksForTeam}` : ''}
                    onFooterLabelClick={
                      tricksForTeam > 0 && showLastTrickButton ? onPeekLastTrick : undefined
                    }
                    nameActionBadge={nameActionBadge}
                  >
                    <span
                      className={cn(
                        'inline-flex max-w-full min-w-0 shrink items-center justify-center truncate rounded-md border px-2 py-0.5 text-center text-[10px] font-bold uppercase tracking-wide',
                        teamOfSeat(seatIndex) === 0
                          ? 'border-emerald-500/50 bg-emerald-500/[0.12] text-emerald-300'
                          : 'border-red-500/50 bg-red-500/[0.12] text-red-300',
                      )}
                    >
                      {teamOfSeat(seatIndex) === 0 ? 'МЫ' : 'ОНИ'}
                    </span>
                  </IndustrialSeat>
                  <BelotSeatSignalStrip
                    phase={phase}
                    declarationMode={declarationMode}
                    declaredComboKeys={stripKeys}
                    casualDeclareHand={casualDeclareHand}
                    declarationBattleOutcome={declarationStripBattleOutcome}
                    declarationBattleApply={declarationStripBattleApply}
                    seatTeamIndex={seatTeamIx}
                  />
                </div>
              </div>
            );
          })}
        </div>

        {showTableAuctionRiver ? (
          <div className="pointer-events-none absolute inset-x-0 bottom-[14%] z-[12] flex justify-center px-2 sm:bottom-[16%]">
            <div className="pointer-events-auto w-full max-w-[min(100%,22rem)] rounded-xl border border-white/12 bg-black/60 px-2 py-1.5 shadow-lg backdrop-blur-md sm:px-3 sm:py-2">
              <BelotAuctionMovesRiver
                variant="table"
                auctionLog={auctionLog}
                seatDisplayName={(sn) => {
                  const r = belotSeatRows.find((x) => x.seat === sn);
                  return r?.name || (sn === heroIndex ? heroName : `Игрок ${sn}`);
                }}
                heroName={heroName}
                onOpenFullLog={() => setAuctionLogOpen(true)}
              />
            </div>
          </div>
        ) : null}

        {shulerFx.caught || shulerFx.falseAccuse ? (
          <div
            className="pointer-events-none absolute inset-0"
            style={{ zIndex: BELOT_FX_Z_CRITICAL_OVERLAY }}
            aria-hidden
          >
            {seatsForRender.map(({ seatIndex, slot }) => {
              const cfx = shulerFx.caught;
              const ffx = shulerFx.falseAccuse;
              const showHand =
                cfx &&
                cfx.cheaterSeat === seatIndex &&
                shulerCheaterHandSorted.length > 0;
              const showPlus = cfx && cfx.accuserSeat === seatIndex;
              const showMinus = ffx && ffx.accuserSeat === seatIndex;
              if (!showHand && !showPlus && !showMinus) return null;
              const fanN = shulerCheaterHandSorted.length || 1;
              const fanMetrics = computeHandFanMetrics(fanN, isCompact);
              return (
                <div
                  key={`shuler-ol-${seatIndex}-${cfx?.token ?? ffx?.token ?? 0}`}
                  className="absolute"
                  style={{
                    left: `${slot.leftPct}%`,
                    top: `${slot.topPct}%`,
                    transform: 'translate(-50%, -50%)',
                    transformOrigin: 'center',
                  }}
                >
                  {showPlus ? (
                    <div className="absolute -top-14 left-1/2 z-[1] -translate-x-1/2 whitespace-nowrap rounded-lg border border-emerald-500/50 bg-emerald-950/90 px-2 py-1 text-[11px] font-black tabular-nums text-emerald-100 shadow-lg animate-[app-toast-in_260ms_cubic-bezier(0.2,0.8,0.2,1)] motion-reduce:animate-none">
                      +16 <span className="font-semibold text-emerald-200/90">тенсов</span>
                      {BELOT_FX_FUN_EMOJI ? <span className="ml-0.5">🐭</span> : null}
                    </div>
                  ) : null}
                  {showMinus ? (
                    <div className="absolute -top-14 left-1/2 z-[1] -translate-x-1/2 whitespace-nowrap rounded-lg border border-red-500/55 bg-red-950/90 px-2 py-1 text-[11px] font-black tabular-nums text-red-100 shadow-lg animate-[app-toast-in_260ms_cubic-bezier(0.2,0.8,0.2,1)] motion-reduce:animate-none">
                      −10 <span className="font-semibold text-red-200/90">тенсов</span>
                      {BELOT_FX_FUN_EMOJI ? <span className="ml-0.5">🐭</span> : null}
                    </div>
                  ) : null}
                  {showHand && cfx ? (
                    <div
                      className={cn(
                        'relative flex items-end justify-center opacity-100',
                        shulerFx.animated &&
                          'motion-safe:animate-[app-toast-in_220ms_cubic-bezier(0.2,0.8,0.2,1)]',
                      )}
                      style={{
                        marginTop: isCompact ? '-4.5rem' : '-5.25rem',
                        width: `${Math.min(fanMetrics.fanWidth, 240)}px`,
                        height: isCompact ? '4.5rem' : '5rem',
                      }}
                    >
                      {shulerCheaterHandSorted.map((cardId, idx) => {
                        const { rank, suit } = belotCardIdToPlaying(cardId);
                        const isTr = belotCardIsTrump(trumpSuit, cardId);
                        const n = shulerCheaterHandSorted.length;
                        const centerOffset = idx - (n - 1) / 2;
                        const spreadFactor = n > 1 ? centerOffset / ((n - 1) / 2) : 0;
                        const rotate = spreadFactor * (isCompact ? 10 : 12);
                        const curveY = Math.abs(spreadFactor) * (isCompact ? 6 : 8);
                        const translateX = centerOffset * (fanMetrics.fanStep * 0.55);
                        return (
                          <div
                            key={`shuler-reveal-${cfx.token}-${cardId}-${idx}`}
                            className="absolute bottom-0 left-1/2"
                            style={{
                              zIndex: idx + 1,
                              transform: `translateX(calc(-50% + ${translateX}px)) translateY(${curveY}px) rotate(${rotate}deg)`,
                            }}
                          >
                            <PlayingCard
                              rank={rank}
                              suit={suit}
                              size="sm"
                              className={cn(
                                'shadow-xl ring-1 ring-white/15',
                                isTr && 'ring-2 ring-amber-300/55',
                              )}
                            />
                          </div>
                        );
                      })}
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        ) : null}

        <BelotOnlineRevealOverlay
          comboFx={comboRevealFx}
          fx={null}
          embedded
          zIndex={BELOT_FX_Z_COMBO_FAN}
        />

        <div
          ref={trickCenterRef}
          className="pointer-events-none absolute left-1/2 top-1/2 z-[19] h-[28%] w-[32%] -translate-x-1/2 -translate-y-1/2"
          aria-hidden
        />
        {dragPlayCard && inPlay ? (
          <div className="pointer-events-none absolute left-1/2 top-1/2 z-[19] -translate-x-1/2 -translate-y-1/2">
            <div
              className={cn(
                'h-[10.8rem] w-24 max-h-[66vmin] max-w-[48vmin] shrink-0 rounded-[50%] border-2 transition-all duration-100',
                dragPlayCard.dropActive
                  ? 'border-cyan-300/95 bg-cyan-500/18 shadow-[0_0_28px_rgba(34,211,238,0.5)]'
                  : 'border-cyan-400/55 bg-cyan-500/10 shadow-[0_0_18px_rgba(34,211,238,0.26)]',
              )}
            />
          </div>
        ) : null}

        {currentTrick.length > 0 && (inPlay || phase === 'dealing') ? (
          <div className="pointer-events-none absolute inset-0 z-20">
            {currentTrick.map((entry, idx) => {
              const seat = typeof entry?.seat === 'number' ? entry.seat : idx;
              const card = entry?.card;
              const position = getRelativeSeatPosition(seat, heroIndex);
              const isTr = belotCardIsTrump(trumpSuit, card);
              const { rank, suit } = belotCardIdToPlaying(card);
              const canAccuseTap =
                Boolean(shulerEnabled) &&
                typeof onAccuseDirty === 'function' &&
                inPlay &&
                heroIndex != null &&
                teamOfSeat(seat) !== teamOfSeat(heroIndex);
              return (
                <div
                  key={`online-trick-${seat}-${idx}`}
                  className="absolute left-1/2 top-1/2"
                  style={{ transform: TRICK_SLOT_TRANSFORM[position], zIndex: 10 + idx }}
                >
                  <div className="relative">
                    <PlayingCard
                      rank={rank}
                      suit={suit}
                      size="sm"
                      className={cn('shadow-2xl', isTr && 'ring-2 ring-amber-300/55')}
                    />
                    {canAccuseTap ? (
                      <button
                        type="button"
                        className="pointer-events-auto absolute inset-0 z-[25] cursor-crosshair rounded-md ring-0 transition hover:bg-red-500/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-400/70"
                        aria-label="Схватить за руку"
                        title="Схватить за руку"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          onAccuseDirty(card);
                        }}
                      />
                    ) : null}
                    <span className="pointer-events-none absolute -bottom-4 left-1/2 max-w-[5rem] -translate-x-1/2 truncate whitespace-nowrap rounded bg-black/55 px-1 py-0.5 text-[9px] text-white/80">
                      {belotSeatRows.find((r) => r.seat === seat)?.name || `м${seat}`}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        ) : null}
          {trickEndOverlay?.plays?.length === 4 ? (
            <div className="pointer-events-none absolute inset-0 z-[22]">
              {trickEndOverlay.plays.map((entry, idx) => {
                const seat = typeof entry?.seat === 'number' ? entry.seat : idx;
                const card = entry?.card;
                const position = getRelativeSeatPosition(seat, heroIndex);
                const isTr = belotCardIsTrump(trumpSuit, card);
                const { rank, suit } = belotCardIdToPlaying(card);
                const isWinningReveal =
                  trickEndOverlay.winnerSeat != null && trickEndOverlay.winnerSeat === seat;
                return (
                  <div
                    key={`online-trick-end-${trickEndOverlay.token}-${seat}-${idx}`}
                    className="absolute left-1/2 top-1/2"
                    style={{ transform: TRICK_SLOT_TRANSFORM[position], zIndex: 10 + idx }}
                  >
                    <div className="relative">
                      <PlayingCard
                        rank={rank}
                        suit={suit}
                        size="sm"
                        className={cn(
                          'shadow-2xl',
                          isTr && 'ring-2 ring-amber-300/55',
                          isWinningReveal && 'ring-2 ring-neon-gold ring-offset-1 ring-offset-black/35',
                        )}
                      />
                      <span className="absolute -bottom-4 left-1/2 max-w-[5rem] -translate-x-1/2 truncate whitespace-nowrap rounded bg-black/55 px-1 py-0.5 text-[9px] text-white/80">
                        {belotSeatRows.find((r) => r.seat === seat)?.name || `м${seat}`}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : null}
      </div>

      {heroAuctionPanel ? (
        <div className="z-20 shrink-0 border-t border-border/40 bg-background/95 px-1.5 pb-0 pt-1 sm:px-2 sm:pt-1.5">
          {heroAuctionPanel}
        </div>
      ) : null}

      {sortedHand.length > 0 ? (
        <div
          className={cn(
            'mx-auto w-full max-w-2xl',
            heroAuctionPanel ? 'pt-0' : 'pt-0.5 sm:pt-1',
          )}
        >
          <div
            className={cn(
              'rounded-xl border border-neon-cyan/25 bg-card/55',
              isCompact ? 'p-1 sm:p-1.5' : 'p-1.5 sm:p-2',
            )}
          >
            <div
              className={cn(
                'flex min-h-0 items-stretch justify-center overflow-hidden pb-0 pt-1 sm:pt-2',
                isCompact
                  ? 'h-[198px] sm:h-[220px] md:h-[236px]'
                  : 'h-[168px] sm:h-[184px] md:h-[196px]',
              )}
            >
              <div
                className="relative h-full min-h-0 min-w-0 [filter:drop-shadow(0_0_15px_rgba(0,255,255,0.35))]"
                style={{ width: `${handFanMetrics.fanWidth}px` }}
              >
                {inPlay && canPlay && !roundDealAnimActive && sortedHand.length > 0 ? (
                  <div
                    className={cn(
                      'pointer-events-none absolute left-1/2 bottom-0 z-[1] -translate-x-1/2 translate-y-1 rounded-[50%] border-2 border-neon-gold/50 bg-gradient-to-t from-neon-gold/14 via-neon-gold/5 to-transparent shadow-[0_0_32px_rgba(255,215,0,0.22)]',
                      isCompact
                        ? 'h-[6.5rem] w-[min(100%,17rem)] max-w-[94%]'
                        : 'h-[7.35rem] w-[min(100%,19rem)] max-w-[92%]',
                    )}
                    aria-hidden
                  />
                ) : null}
                {sortedHand.map((cardId, idx) => {
                  const { handCount, fanStep, fanBaseYOffset } = handFanMetrics;
                  const centerOffset = idx - (handCount - 1) / 2;
                  const spreadFactor = handCount > 1 ? centerOffset / ((handCount - 1) / 2) : 0;
                  const rotate = spreadFactor * (isCompact ? 14 : 18);
                  const curveY = Math.abs(spreadFactor) * (isCompact ? 14 : 20);
                  const translateX = centerOffset * fanStep;
                  const { rank, suit } = belotCardIdToPlaying(cardId);
                  const cardPlayable = canPlay;
                  const shulerDirtyHint =
                    Boolean(shulerEnabled) &&
                    canPlay &&
                    shulerLegalCardIds instanceof Set &&
                    !shulerLegalCardIds.has(cardId);
                  const liftY = hoveredCardIndex === idx && cardPlayable ? (isCompact ? 18 : 24) : 0;
                  const baseScale = cardPlayable ? (isCompact ? 1.1 : 1.2) : 1;
                  const scale =
                    hoveredCardIndex === idx && cardPlayable
                      ? isCompact
                        ? 1.14
                        : 1.24
                      : baseScale;
                  const baseZIndex = cardPlayable ? 80 + idx : idx + 1;
                  const isPendingHidden = pendingHiddenCardId === cardId;
                  const isTr = belotCardIsTrump(trumpSuit, cardId);
                  const heroRevealRow = seatDealSlotRevealed?.[heroIndex];
                  const playPayload = { card: cardId, from: /** @type {const} */ ('hand') };
                  if (roundDealAnimActive && sortedHand.length === 8) {
                    const slotRevealed = Boolean(heroRevealRow?.[idx]);
                    return (
                      <div
                        key={`online-deal-slot-${idx}`}
                        ref={handSlotRefs[idx]}
                        className="absolute bottom-0 left-1/2"
                        style={{
                          zIndex: idx + 1,
                          transform: `translateX(calc(-50% + ${translateX}px)) translateY(${fanBaseYOffset + curveY}px) rotate(${rotate}deg)`,
                        }}
                        aria-hidden
                      >
                        {slotRevealed ? (
                          <div className="pointer-events-none">
                            <PlayingCard faceDown size="md" />
                          </div>
                        ) : (
                          <div className="pointer-events-none h-[5.5rem] w-16 shrink-0 opacity-0" />
                        )}
                      </div>
                    );
                  }
                  return (
                    <button
                      key={`online-hand-${cardId}-${idx}`}
                      ref={handSlotRefs[idx]}
                      type="button"
                      onClick={() => onPlayCardClick(playPayload, cardPlayable)}
                      disabled={!cardPlayable}
                      title={`id ${cardId}`}
                      onPointerDown={(ev) => onPlayPointerDown(ev, playPayload, cardPlayable)}
                      onPointerMove={onPlayPointerMove}
                      onPointerUp={onPlayPointerUp}
                      onPointerCancel={onPlayPointerCancel}
                      onMouseEnter={() => setHoveredCardIndex(idx)}
                      onMouseLeave={() => setHoveredCardIndex(null)}
                      onFocus={() => setHoveredCardIndex(idx)}
                      onBlur={() => setHoveredCardIndex(null)}
                      className={cn(
                        'absolute bottom-0 left-1/2 appearance-none border-0 bg-transparent p-0 transition-[transform,filter] ease-[cubic-bezier(0.2,0.82,0.2,1)] focus:outline-none touch-none select-none',
                        dragPlayCard ? 'duration-0' : 'duration-300',
                        cardPlayable ? 'cursor-pointer' : 'cursor-not-allowed opacity-65',
                      )}
                      style={{
                        zIndex: hoveredCardIndex === idx ? 200 : baseZIndex,
                        transform: `translateX(calc(-50% + ${translateX}px)) translateY(${fanBaseYOffset + curveY - liftY}px) rotate(${rotate}deg) scale(${scale})`,
                      }}
                    >
                      {isPendingHidden ? (
                        <div className="h-[5.5rem] w-16 rounded-md border border-cyan-300/25 bg-cyan-500/10 opacity-35" />
                      ) : (
                        <PlayingCard
                          rank={rank}
                          suit={suit}
                          size="md"
                          highlighted={cardPlayable}
                          className={cn(
                            cardPlayable &&
                              'ring-1 ring-neon-gold/55 shadow-[0_6px_22px_rgba(255,215,0,0.38)]',
                            !cardPlayable && 'grayscale-[0.1]',
                            isTr && 'shadow-[0_0_18px_rgba(251,191,36,0.32)]',
                          )}
                        />
                      )}
                      {shulerDirtyHint ? (
                        <span
                          className="pointer-events-none absolute -right-0.5 -top-1 flex h-[18px] min-w-[18px] items-center justify-center rounded-full border border-red-400/90 bg-red-950/95 px-0.5 text-[10px] font-black leading-none text-red-100 shadow-md"
                          aria-hidden
                        >
                          ?
                        </span>
                      ) : null}
                      <span
                        className={cn(
                          'absolute bottom-1 left-1/2 -translate-x-1/2 rounded bg-black/55 px-1 py-px font-mono text-[8px]',
                          belotCardColorClass(cardId),
                        )}
                        aria-hidden
                      >
                        {isTr ? '★' : ''}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {cardFly ? (
        <div ref={flyCardWrapRef} className="pointer-events-none fixed z-[200]" aria-hidden>
          <PlayingCard rank={cardFly.rank} suit={cardFly.suit} size="md" className="shadow-2xl" />
        </div>
      ) : null}
      {dragPlayCard ? (
        <div
          ref={dragGhostRef}
          className="pointer-events-none fixed z-[205]"
          style={{
            left: dragPlayCard.x,
            top: dragPlayCard.y,
            transform: `translate(-50%, -64%) rotate(5deg) scale(${dragPlayCard.returning ? 1 : 1.04})`,
            filter: dragPlayCard.dropActive
              ? 'drop-shadow(0 0 24px rgba(34,211,238,0.55))'
              : 'drop-shadow(0 0 18px rgba(0,0,0,0.45))',
          }}
          aria-hidden
        >
          <PlayingCard
            rank={dragPlayCard.rank}
            suit={dragPlayCard.suit}
            size="md"
            className={cn(
              'shadow-2xl transition-all duration-75',
              dragPlayCard.dropActive && 'ring-2 ring-cyan-300/80 ring-offset-2 ring-offset-black/25',
            )}
          />
        </div>
      ) : null}

      {bottomActions ? (
        <div className="z-20 mx-auto mt-0.5 flex w-full max-w-2xl flex-wrap gap-1.5 rounded-xl border border-border/45 bg-background/82 p-1.5 backdrop-blur-md sm:mt-1 sm:gap-2 sm:p-2 sm:mb-1">
          {bottomActions}
        </div>
      ) : null}

      {inAuction && auctionLogOpen ? (
        <div
          className="fixed inset-0 z-[80] flex items-end justify-center bg-black/45 p-3 backdrop-blur-[2px] sm:items-center"
          onClick={() => setAuctionLogOpen(false)}
        >
          <div
            className="w-full max-w-md rounded-xl border border-border/65 bg-[#0e1523]/96 p-3 shadow-[0_18px_40px_rgba(0,0,0,0.45)]"
            onClick={(ev) => ev.stopPropagation()}
          >
            <div className="mb-2 flex items-center justify-between">
              <p className="text-[11px] font-black uppercase tracking-wide text-amber-100">Лог торгов</p>
              <button
                type="button"
                onClick={() => setAuctionLogOpen(false)}
                className="rounded-md border border-border/50 px-2 py-1 text-[10px] text-muted-foreground hover:bg-muted/20 hover:text-foreground"
              >
                Закрыть
              </button>
            </div>
            <div className="max-h-[44vh] space-y-1.5 overflow-y-auto pr-1">
              {Array.isArray(auctionLog) && auctionLog.length > 0 ? (
                auctionLog.map((row, idx) => (
                  <div
                    key={row.id || `auction-log-${idx}`}
                    className="rounded-md border border-border/45 bg-black/25 px-2 py-1.5 text-[11px] animate-[app-toast-in_260ms_cubic-bezier(0.2,0.8,0.2,1)]"
                  >
                    <span className="font-semibold text-slate-100">
                      {belotSeatRows.find((s) => s.seat === row.seat)?.name || `м${row.seat}`}
                    </span>
                    <span className="text-slate-400">: </span>
                    <span className="text-amber-100">{row.label}</span>
                  </div>
                ))
              ) : (
                <p className="text-center text-[11px] text-muted-foreground">Пока без записей</p>
              )}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
